import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  VictoryArea,
  VictoryAxis,
  VictoryChart,
  VictoryLabel,
  VictoryTooltip,
  VictoryScatter,
  Point,
  VictoryLine,
} from 'victory';
import { DomainTuple } from 'victory-core';
import moment from 'moment';
import ChartOptions from './ChartOptions';
import {
  CompactTransaction,
  Subscription,
} from '../../../../../types/subscription';
import { DataPoint } from '../../../../Detail/Charts/types';
import { ComponentDimensions } from '../../../../../types/Layout';
import {
  formatBalance,
  formatPercentage,
  formatPrice,
} from '../../../../../helpers/NumberFormatter';
import { colors } from '../../../../../colors';
import { getBotColor } from '../../../../Overview/ClusterItem/Charts/helpers';
import {
  calculateIsColorTooDark,
  lightenColor,
} from '../../../../../helpers/colors';
import {
  createXTickValues,
  getFormatUnit,
  isCustomTransaction,
} from '../../../../../helpers/bundleTransactionsToCompactForm';

import './index.css';
import { CustomTransaction } from 'grts-utils/types/Transaction';
import {
  calculateLongestTickWidth,
  calculateYAxisWidth,
  calculateYTicks,
  stretchYValueOnSharedDomain,
} from '../../../../../helpers/Chart';
import { Bot } from 'grts-utils/types/Bot';
import useAnimatedChartData from '../../../../../hooks/useAnimatedChartData';
import { useSelector } from 'react-redux';
import { selectChartData } from '../../../../../store/slices/chartData';

interface Props {
  subscription: Subscription;
  bot: Bot | undefined;
}

export enum ChartType {
  TotalStableBalance = 'TOTAL_STABLE_VALUE',
  TotalAssetBalance = 'TOTAL_ASSET_VALUE',
  StableBalance = 'STABLE_VALUE',
  AssetBalance = 'ASSET_VALUE',
  NoAlgoritmicProgression = 'NO_ALGORITMIC_PROGRESSION',
}

type Datum = {
  x: number;
  y: number;
  color: string;
  isBuy: boolean | null;
};

const xDomainBuffer = 0.1;

const ProfitChart = (props: Props) => {
  const { subscription, bot } = props;
  const { compactTransactions } = subscription;

  const data = useSelector(selectChartData(subscription.id));

  const [selectedChartTypes, setSelectedChartTypes] = useState([
    ChartType.TotalStableBalance,
    ChartType.NoAlgoritmicProgression,
  ]);
  const [showAccumulativeData, setShowAccumulativeData] = useState(false);

  const [zoomedDomain, setZoomedDomain] = useState<{
    x: DomainTuple;
    y: DomainTuple;
  }>();
  const [zoomedStartIndex, setZoomedStartIndex] = useState<number>();
  const [zoomLevel, setZoomLevel] = useState<number>(1);

  const [rootDimensions, setRootDimensions] = useState<
    ComponentDimensions | undefined
  >(undefined);
  const rootRef = useRef<any>(null);

  useEffect(() => {
    if (!rootRef.current) {
      return;
    }

    if (!!rootDimensions) {
      return;
    }

    setRootDimensions({
      height: rootRef.current.clientHeight * 0.8,
      width: rootRef.current.clientWidth,
    });
  }, [rootDimensions]);

  const selectedChartTypesAxis = useMemo(() => {
    const stable = selectedChartTypes.find((ct) => ct.includes('STABLE'));
    const asset = selectedChartTypes.find((ct) => ct.includes('ASSET'));
    const nap = selectedChartTypes.find((ct) => ct.includes('NO_'));

    const axis = [];

    if (stable) {
      axis.push(stable);
    }

    if (asset) {
      axis.push(asset);
    }

    if (nap && !stable) {
      axis.push(ChartType.NoAlgoritmicProgression);
    }

    return axis;
  }, [selectedChartTypes]);

  const selectedStableValueChartTypes: ChartType[] = useMemo(() => {
    return selectedChartTypes.filter(
      (ct) =>
        ct === ChartType.NoAlgoritmicProgression ||
        ct === ChartType.TotalStableBalance ||
        ct === ChartType.StableBalance,
    );
  }, [selectedChartTypes]);

  const selectedAssetValueChartTypes: ChartType[] = useMemo(() => {
    return selectedChartTypes.filter(
      (ct) =>
        ct === ChartType.TotalAssetBalance || ct === ChartType.AssetBalance,
    );
  }, [selectedChartTypes]);

  /** ====================================================   Total stable data points ==================================================== */
  const rawTotalStableDataPoints = useMemo(() => {
    return (data?.dataPoints.total.stable ?? [])
      .map((dataPoint: DataPoint) => ({
        x: dataPoint.timestamp,
        y: dataPoint.value,
      }))
      .filter(
        (d) =>
          !zoomedDomain ||
          ((zoomedDomain.x[0] as number) -
            ((zoomedDomain.x[1] as number) - (zoomedDomain.x[0] as number)) *
              xDomainBuffer <=
            d.x &&
            d.x <=
              (zoomedDomain.x[1] as number) +
                ((zoomedDomain.x[1] as number) -
                  (zoomedDomain.x[0] as number)) *
                  xDomainBuffer),
      );
  }, [data?.dataPoints.total.stable, zoomedDomain]);
  const rawNapDataPoints = useMemo(() => {
    return (data?.dataPoints.noAlgorithmicProgression ?? [])
      .map((dataPoint: DataPoint) => ({
        x: dataPoint.timestamp,
        y: dataPoint.value,
      }))
      .filter(
        (d) =>
          !zoomedDomain ||
          ((zoomedDomain.x[0] as number) -
            ((zoomedDomain.x[1] as number) - (zoomedDomain.x[0] as number)) *
              xDomainBuffer <=
            d.x &&
            d.x <=
              (zoomedDomain.x[1] as number) +
                ((zoomedDomain.x[1] as number) -
                  (zoomedDomain.x[0] as number)) *
                  xDomainBuffer),
      );
  }, [data?.dataPoints.noAlgorithmicProgression, zoomedDomain]);
  const rawStableDataPoints = useMemo(() => {
    return (data?.dataPoints.stable?.all ?? [])
      .map((dataPoint: DataPoint) => ({
        x: dataPoint.timestamp,
        y: dataPoint.value,
      }))
      .filter(
        (d) =>
          !zoomedDomain ||
          ((zoomedDomain.x[0] as number) -
            ((zoomedDomain.x[1] as number) - (zoomedDomain.x[0] as number)) *
              xDomainBuffer <=
            d.x &&
            d.x <=
              (zoomedDomain.x[1] as number) +
                ((zoomedDomain.x[1] as number) -
                  (zoomedDomain.x[0] as number)) *
                  xDomainBuffer),
      );
  }, [data?.dataPoints.stable?.all, zoomedDomain]);

  const rawTotalStableDataPointsMax = useMemo(
    () => Math.max(0, ...rawTotalStableDataPoints.map((datum) => datum.y)),
    [rawTotalStableDataPoints],
  );
  const rawNapDataPointsMax = useMemo(
    () => Math.max(0, ...rawNapDataPoints.map((datum) => datum.y)),
    [rawNapDataPoints],
  );
  const rawStableDataPointsMax = useMemo(
    () => Math.max(0, ...rawStableDataPoints.map((datum) => datum.y)),
    [rawStableDataPoints],
  );

  const stableValueMax = useMemo(() => {
    return Math.max(
      0,
      ...selectedStableValueChartTypes.map((ct) => {
        switch (ct) {
          case ChartType.NoAlgoritmicProgression:
            return rawNapDataPointsMax;

          case ChartType.TotalStableBalance:
            return rawTotalStableDataPointsMax;

          case ChartType.StableBalance:
            return rawStableDataPointsMax;

          default:
            return 0;
        }
      }),
    );
  }, [
    rawNapDataPointsMax,
    rawStableDataPointsMax,
    rawTotalStableDataPointsMax,
    selectedStableValueChartTypes,
  ]);

  const totalStableDataOriginalDomain = useMemo(() => {
    return rawTotalStableDataPoints.map((datum) => ({
      ...datum,
      y: datum.y / stableValueMax,
    }));
  }, [rawTotalStableDataPoints, stableValueMax]);
  const napDataOriginalDomain = useMemo(() => {
    return rawNapDataPoints.map((datum) => ({
      ...datum,
      y: datum.y / stableValueMax,
    }));
  }, [rawNapDataPoints, stableValueMax]);
  const stableDataOriginalDomain = useMemo(() => {
    return rawStableDataPoints.map((datum) => ({
      ...datum,
      y: datum.y / stableValueMax,
    }));
  }, [rawStableDataPoints, stableValueMax]);

  const stableValueOriginalDomainMin = useMemo(() => {
    return Math.min(
      ...selectedStableValueChartTypes.reduce<number[]>((acc, ct) => {
        switch (ct) {
          case ChartType.NoAlgoritmicProgression:
            acc.push(...napDataOriginalDomain.map((d) => d.y));
            return acc;

          case ChartType.TotalStableBalance:
            acc.push(...totalStableDataOriginalDomain.map((d) => d.y));
            return acc;

          case ChartType.StableBalance:
            acc.push(...stableDataOriginalDomain.map((d) => d.y));
            return acc;

          default:
            return acc;
        }
      }, []),
    );
  }, [
    napDataOriginalDomain,
    selectedStableValueChartTypes,
    stableDataOriginalDomain,
    totalStableDataOriginalDomain,
  ]);

  const stableValueOriginalDomainMax = useMemo(() => {
    return Math.max(
      0,
      ...selectedStableValueChartTypes.reduce<number[]>((acc, ct) => {
        switch (ct) {
          case ChartType.NoAlgoritmicProgression:
            acc.push(...napDataOriginalDomain.map((d) => d.y));
            return acc;

          case ChartType.TotalStableBalance:
            acc.push(...totalStableDataOriginalDomain.map((d) => d.y));
            return acc;

          case ChartType.StableBalance:
            acc.push(...stableDataOriginalDomain.map((d) => d.y));
            return acc;

          default:
            return acc;
        }
      }, []),
    );
  }, [
    napDataOriginalDomain,
    selectedStableValueChartTypes,
    stableDataOriginalDomain,
    totalStableDataOriginalDomain,
  ]);

  /** ==================================================== N.A.P data points ==================================================== */

  const napData = useMemo(() => {
    return napDataOriginalDomain.map((datum) => ({
      ...datum,
      y: stretchYValueOnSharedDomain(
        datum.y,
        stableValueOriginalDomainMin,
        stableValueOriginalDomainMax,
      ),
    }));
  }, [
    napDataOriginalDomain,
    stableValueOriginalDomainMin,
    stableValueOriginalDomainMax,
  ]);

  const totalStableData = useMemo(() => {
    return totalStableDataOriginalDomain.map((datum) => ({
      ...datum,
      y: stretchYValueOnSharedDomain(
        datum.y,
        stableValueOriginalDomainMin,
        stableValueOriginalDomainMax,
      ),
    }));
  }, [
    totalStableDataOriginalDomain,
    stableValueOriginalDomainMin,
    stableValueOriginalDomainMax,
  ]);

  const stableData = useMemo(() => {
    return stableDataOriginalDomain.map((datum) => ({
      ...datum,
      y: stretchYValueOnSharedDomain(
        datum.y,
        stableValueOriginalDomainMin,
        stableValueOriginalDomainMax,
      ),
    }));
  }, [
    stableDataOriginalDomain,
    stableValueOriginalDomainMin,
    stableValueOriginalDomainMax,
  ]);

  const isPositiveChart = useMemo(() => {
    if (rawTotalStableDataPoints.length < 2) {
      return false;
    }

    return (
      rawTotalStableDataPoints[rawTotalStableDataPoints.length - 1].y -
        rawTotalStableDataPoints[0].y >
      0
    );
  }, [rawTotalStableDataPoints]);

  /** ==================================================== Total asset data points ==================================================== */
  const rawTotalAssetDataPoints = useMemo(() => {
    return (data?.dataPoints.total.asset ?? [])
      .map((dataPoint: DataPoint) => ({
        x: dataPoint.timestamp,
        y: dataPoint.value,
      }))
      .filter(
        (d) =>
          !zoomedDomain ||
          ((zoomedDomain.x[0] as number) -
            ((zoomedDomain.x[1] as number) - (zoomedDomain.x[0] as number)) *
              xDomainBuffer <=
            d.x &&
            d.x <=
              (zoomedDomain.x[1] as number) +
                ((zoomedDomain.x[1] as number) -
                  (zoomedDomain.x[0] as number)) *
                  xDomainBuffer),
      );
  }, [data?.dataPoints.total.asset, zoomedDomain]);
  const rawAssetDataPoints = useMemo(() => {
    return (data?.dataPoints.asset?.all ?? [])
      .map((dataPoint: DataPoint) => ({
        x: dataPoint.timestamp,
        y: dataPoint.value,
      }))
      .filter(
        (d) =>
          !zoomedDomain ||
          ((zoomedDomain.x[0] as number) -
            ((zoomedDomain.x[1] as number) - (zoomedDomain.x[0] as number)) *
              xDomainBuffer <=
            d.x &&
            d.x <=
              (zoomedDomain.x[1] as number) +
                ((zoomedDomain.x[1] as number) -
                  (zoomedDomain.x[0] as number)) *
                  xDomainBuffer),
      );
  }, [data?.dataPoints.asset?.all, zoomedDomain]);

  const rawTotalAssetDataPointsMax = useMemo(
    () => Math.max(0, ...rawTotalAssetDataPoints.map((datum) => datum.y)),
    [rawTotalAssetDataPoints],
  );

  const rawAssetDataPointsMax = useMemo(
    () => Math.max(0, ...rawAssetDataPoints.map((datum) => datum.y)),
    [rawAssetDataPoints],
  );

  const assetValueMax = useMemo(() => {
    return Math.max(
      0,
      ...selectedAssetValueChartTypes.map((ct) => {
        switch (ct) {
          case ChartType.TotalAssetBalance:
            return rawTotalAssetDataPointsMax;

          case ChartType.AssetBalance:
            return rawAssetDataPointsMax;

          default:
            return 0;
        }
      }),
    );
  }, [
    rawAssetDataPointsMax,
    rawTotalAssetDataPointsMax,
    selectedAssetValueChartTypes,
  ]);

  const totalAssetDataOriginalDomain = useMemo(() => {
    return rawTotalAssetDataPoints.map((datum) => ({
      ...datum,
      y: datum.y / assetValueMax,
    }));
  }, [assetValueMax, rawTotalAssetDataPoints]);

  const assetDataOriginalDomain = useMemo(() => {
    return rawAssetDataPoints.map((datum) => ({
      ...datum,
      y: datum.y / assetValueMax,
    }));
  }, [assetValueMax, rawAssetDataPoints]);

  const assetValueOriginalDomainMin = useMemo(() => {
    return Math.min(
      ...selectedAssetValueChartTypes.reduce<number[]>((acc, ct) => {
        switch (ct) {
          case ChartType.TotalAssetBalance:
            acc.push(...totalAssetDataOriginalDomain.map((d) => d.y));
            return acc;

          case ChartType.AssetBalance:
            acc.push(...assetDataOriginalDomain.map((d) => d.y));
            return acc;

          default:
            return acc;
        }
      }, []),
    );
  }, [
    assetDataOriginalDomain,
    selectedAssetValueChartTypes,
    totalAssetDataOriginalDomain,
  ]);

  const assetValueOriginalDomainMax = useMemo(() => {
    return Math.max(
      0,
      ...selectedAssetValueChartTypes.reduce<number[]>((acc, ct) => {
        switch (ct) {
          case ChartType.TotalAssetBalance:
            acc.push(...totalAssetDataOriginalDomain.map((d) => d.y));
            return acc;

          case ChartType.AssetBalance:
            acc.push(...assetDataOriginalDomain.map((d) => d.y));
            return acc;

          default:
            return acc;
        }
      }, []),
    );
  }, [
    assetDataOriginalDomain,
    selectedAssetValueChartTypes,
    totalAssetDataOriginalDomain,
  ]);

  const totalAssetData = useMemo(() => {
    return totalAssetDataOriginalDomain.map((datum) => ({
      ...datum,
      y: stretchYValueOnSharedDomain(
        datum.y,
        assetValueOriginalDomainMin,
        assetValueOriginalDomainMax,
      ),
    }));
  }, [
    assetValueOriginalDomainMax,
    assetValueOriginalDomainMin,
    totalAssetDataOriginalDomain,
  ]);

  const assetData = useMemo(() => {
    return assetDataOriginalDomain.map((datum) => ({
      ...datum,
      y: stretchYValueOnSharedDomain(
        datum.y,
        assetValueOriginalDomainMin,
        assetValueOriginalDomainMax,
      ),
    }));
  }, [
    assetDataOriginalDomain,
    assetValueOriginalDomainMax,
    assetValueOriginalDomainMin,
  ]);

  const getTransactionTotalValue = useCallback(
    (transaction: any /*Transaction | CustomTransaction*/) => {
      return (
        transaction.currentStableBalance +
        transaction.currentAssetBalance *
          (transaction.orderPrice ?? transaction.currentPrice)
      );
    },
    [],
  );

  const getTransactionTotalAssetValue = useCallback(
    (transaction: any /*Transaction | CustomTransaction*/) => {
      return (
        transaction.currentStableBalance /
          (transaction.orderPrice ?? transaction.currentPrice) +
        transaction.currentAssetBalance
      );
    },
    [],
  );

  const calculateTransactionDotYValue = useCallback(
    (transaction: CompactTransaction | CustomTransaction) => {
      let dataSet = [];

      if (selectedChartTypes.includes(ChartType.TotalStableBalance)) {
        dataSet = totalStableData;
      } else if (selectedChartTypes.includes(ChartType.TotalAssetBalance)) {
        dataSet = totalAssetData;
      } else if (
        selectedChartTypes.includes(ChartType.NoAlgoritmicProgression)
      ) {
        dataSet = napData;
      } else if (selectedChartTypes.includes(ChartType.StableBalance)) {
        dataSet = stableData;
      } else {
        dataSet = assetData;
      }

      return dataSet.reduce(
        (acc, curr) => {
          const diffData = Math.abs(
            acc.timestamp - (transaction.transactionTimestamp || 0),
          );
          const diffTransaction = Math.abs(
            curr.x - (transaction.transactionTimestamp || 0),
          );

          if (diffTransaction < diffData) {
            acc.timestamp = curr.x;
            acc.value = curr.y;
          }

          return acc;
        },
        { timestamp: 0, value: 0 },
      );
    },
    [
      assetData,
      napData,
      selectedChartTypes,
      stableData,
      totalAssetData,
      totalStableData,
    ],
  );

  const generateTooltipLabel = useCallback(
    (index: number, transaction: CompactTransaction | CustomTransaction) => {
      const formattedDate = moment(transaction.transactionTimestamp).format(
        'DD MMM YYYY',
      );

      if (isCustomTransaction(transaction)) {
        const transactionType = transaction.transactionType;
        const isStableBalance = transactionType.indexOf('STABLE') !== -1;
        const stableAmount = formatBalance(transaction.stableQuantity, true);
        const assetAmount = `${formatBalance(
          transaction.assetQuantity,
          false,
        )} ${subscription.baseAsset}`;

        const amount = isStableBalance
          ? `${stableAmount} (${assetAmount})`
          : `${assetAmount} (${stableAmount})`;

        return `${index + 1}. ${formattedDate}\n${transactionType}\n${amount}`;
      }

      const isBuy = transaction.orderType === 'BUY';

      const stableBalance = formatBalance(
        getTransactionTotalValue(transaction),
      );
      const assetBalance = `${formatBalance(
        getTransactionTotalAssetValue(transaction),
        false,
      )} ${subscription.baseAsset}`;
      const balance = isBuy ? assetBalance : stableBalance;

      const profit = formatPercentage(transaction.profit ?? 0);

      return `${index + 1}. ${formattedDate}\n${balance} | ${profit}`;
    },
    [
      subscription.baseAsset,
      getTransactionTotalAssetValue,
      getTransactionTotalValue,
    ],
  );

  const originalSelectedDataPoints = useMemo(
    () =>
      !data
        ? []
        : selectedChartTypes.map((chartType) => {
            switch (chartType) {
              case ChartType.TotalStableBalance:
                return data.dataPoints.total.stable.map((d) => ({
                  x: d.timestamp,
                  y: d.value,
                }));

              case ChartType.TotalAssetBalance:
                return data.dataPoints.total.asset?.map((d) => ({
                  x: d.timestamp,
                  y: d.value,
                }));

              case ChartType.StableBalance:
                return data.dataPoints.stable?.all.map((d) => ({
                  x: d.timestamp,
                  y: d.value,
                }));

              case ChartType.AssetBalance:
                return data.dataPoints.asset?.all.map((d) => ({
                  x: d.timestamp,
                  y: d.value,
                }));

              case ChartType.NoAlgoritmicProgression:
                return data.dataPoints.noAlgorithmicProgression.map((d) => ({
                  x: d.timestamp,
                  y: d.value,
                }));

              default:
                return [];
            }
          }),
    [data, selectedChartTypes],
  );

  const selectedDataPoints = useMemo(
    () =>
      selectedChartTypes.map((chartType) => {
        switch (chartType) {
          case ChartType.TotalStableBalance:
            return totalStableData;

          case ChartType.TotalAssetBalance:
            return totalAssetData;

          case ChartType.StableBalance:
            return stableData;

          case ChartType.AssetBalance:
            return assetData;

          case ChartType.NoAlgoritmicProgression:
            return napData;
        }
      }),
    [
      assetData,
      napData,
      selectedChartTypes,
      stableData,
      totalAssetData,
      totalStableData,
    ],
  );

  const originalDomainX = useMemo(() => {
    let minX = new Date().valueOf();
    let maxX = 0;

    originalSelectedDataPoints.forEach((selectedDataPoint) => {
      selectedDataPoint?.forEach((datum) => {
        minX = Math.min(minX, datum.x);
        maxX = Math.max(maxX, datum.x);
      });
    });

    return [minX, maxX] as DomainTuple;
  }, [originalSelectedDataPoints]);

  const { animatedDataPoints, domain, isAnimatedFinished } =
    useAnimatedChartData(
      subscription.id.toString(),
      selectedDataPoints,
      selectedChartTypes,
      false,
      12,
    );

  // const domain = useMemo(() => {
  //   if (zoomedDomain) {
  //     return zoomedDomain;
  //   }

  //   const isNapVisible = selectedChartTypes.includes(
  //     ChartType.NoAlgoritmicProgression,
  //   );

  //   let minY = isNapVisible ? Infinity : 1;
  //   let maxY = 0;
  //   let minX = new Date().valueOf();
  //   let maxX = 0;

  //   selectedDataPoints.forEach((selectedDataPoint) => {
  //     selectedDataPoint.forEach((datum) => {
  //       minX = Math.min(minX, datum.x);
  //       maxX = Math.max(maxX, datum.x);
  //       minY = Math.min(minY, datum.y);
  //       maxY = Math.max(maxY, datum.y);
  //     });
  //   });

  //   return {
  //     x: [minX, maxX] as DomainTuple,
  //     y: [minY, maxY * 1.03] as DomainTuple,
  //   };
  // }, [selectedChartTypes, selectedDataPoints, zoomedDomain]);

  /** Transactions Data points */
  const transactionDataPoints = useMemo(() => {
    if (!compactTransactions) {
      return [];
    }

    const sortedCompactTransactions = [...compactTransactions].sort(
      (a, b) => a.transactionTimestamp - b.transactionTimestamp,
    );

    return sortedCompactTransactions.map((transaction) => {
      // const isCustomTransaction = transaction.hasOwnProperty('isDistribution');
      const isBuy = !isCustomTransaction(transaction)
        ? transaction.orderType === 'BUY'
        : null;

      const label = generateTooltipLabel(
        transaction.transactionIndex,
        transaction,
      );
      const color = isCustomTransaction(transaction)
        ? colors.blue
        : isBuy
        ? colors.green
        : colors.red;

      // For the y value, let's just search for the closest y value of the main dataSet
      // The values are heavily modified and doesn't show the original data.

      const { timestamp, value } = calculateTransactionDotYValue(transaction);

      return {
        x: timestamp,
        y: value,
        isBuy,
        color,
        label,
      };
    });
  }, [
    calculateTransactionDotYValue,
    compactTransactions,
    generateTooltipLabel,
  ]);

  const axisStyle = useMemo(
    () => ({
      axis: { stroke: 'rgba(64,64,64,0.7)' },
      grid: {
        stroke: 'rgba(64,64,64,0.7)',
      },
      ticks: { stroke: 'white', size: 2 },
      tickLabels: { fontSize: 14, padding: 5, fill: colors.white },
    }),
    [],
  );

  const botColor = useMemo(() => {
    const color = getBotColor(subscription.baseAsset);

    return calculateIsColorTooDark(color) ? lightenColor(color) : color;
  }, [subscription.baseAsset]);

  const tickValues = useMemo(() => {
    if (!domain?.x[0]) {
      return [];
    }

    return createXTickValues(domain.x[0] as number);
  }, [domain?.x]);

  /** Determine on end - start date */
  const calculateTickValue = useCallback(
    (t: Date) => {
      if (!domain?.x) {
        return moment(t).format('DD MMM');
      }

      const formatUnit = getFormatUnit(
        domain.x[0] as number,
        domain.x[1] as number,
      );

      return moment(t).format(formatUnit);
    },
    [domain?.x],
  );

  const calculateYTickValue = useCallback(
    (axisIndex: number, t: any) => {
      const chartType = selectedChartTypesAxis[axisIndex]; // The first axis
      const isStableValue =
        chartType !== ChartType.TotalAssetBalance &&
        chartType !== ChartType.AssetBalance;

      let max = 0;
      let minO = 0;
      let maxO = 0;

      if (
        (ChartType.NoAlgoritmicProgression === chartType ||
          ChartType.TotalStableBalance === chartType ||
          ChartType.StableBalance === chartType) &&
        selectedStableValueChartTypes.includes(chartType)
      ) {
        max = stableValueMax;
        minO = stableValueOriginalDomainMin;
        maxO = stableValueOriginalDomainMax;
      }

      if (
        (ChartType.TotalAssetBalance === chartType ||
          ChartType.AssetBalance === chartType) &&
        selectedAssetValueChartTypes.includes(chartType)
      ) {
        max = assetValueMax;
        minO = assetValueOriginalDomainMin;
        maxO = assetValueOriginalDomainMax;
      }

      /** Reverse the stretch to get the original value */
      const y = maxO - (1 - t) * ((maxO - minO) * 1.05);
      const balance = isNaN(y * max) ? 0 : y * max;

      return `${formatPrice(
        balance,
        isStableValue,
        isStableValue
          ? (maxO - minO) / maxO < 0.01
            ? 2
            : 0
          : balance < 10
          ? bot?.complexParams?.maxDecimals ?? 6
          : 0,
      )}`;
    },
    [
      assetValueMax,
      assetValueOriginalDomainMax,
      assetValueOriginalDomainMin,
      bot?.complexParams?.maxDecimals,
      selectedAssetValueChartTypes,
      selectedChartTypesAxis,
      selectedStableValueChartTypes,
      stableValueMax,
      stableValueOriginalDomainMax,
      stableValueOriginalDomainMin,
    ],
  );

  const getChartColor = useCallback(
    (chartType: ChartType) => {
      switch (chartType) {
        case ChartType.TotalStableBalance:
        case ChartType.StableBalance:
          return {
            fill: `url(#${
              isPositiveChart ? 'positiveStableColor' : 'negativeStableColor'
            })`,
            stroke: isPositiveChart ? colors.mossGreen : colors.red,
          };

        case ChartType.TotalAssetBalance:
        case ChartType.AssetBalance:
          return {
            fill: 'url(#botColor)',
            stroke: botColor,
          };

        case ChartType.NoAlgoritmicProgression:
          return {
            fill: 'url(#napColor)',
            stroke: colors.white,
          };
      }
    },
    [botColor, isPositiveChart],
  );

  const calculateYAxisWidthByChartType = useCallback(
    (index: number) => {
      const yTicks = calculateYTicks(selectedDataPoints[index]);
      const longestTickWidth = calculateLongestTickWidth(yTicks, (t: number) =>
        calculateYTickValue(index, t),
      );
      const yAxisWidth = calculateYAxisWidth(longestTickWidth);
      return { width: yAxisWidth, longestTickWidth };
    },
    [calculateYTickValue, selectedDataPoints],
  );

  // Create information per selected chart type
  const yAxisInformation = useMemo(() => {
    return selectedChartTypesAxis.map((_, index) =>
      calculateYAxisWidthByChartType(index),
    );
  }, [calculateYAxisWidthByChartType, selectedChartTypesAxis]);

  const getYAxisStyle = useCallback(
    (chartType: ChartType, index: number) => {
      const width = yAxisInformation[index]?.width || 0;
      const longestTickWidth = yAxisInformation[index]?.longestTickWidth ?? 0;

      return {
        axis: { stroke: 'rgba(64,64,64,0.7)' },
        grid: {
          stroke: 'rgba(64,64,64,0.7)',
        },
        ticks: { stroke: 'white', size: 2 },
        tickLabels: {
          fontSize: 16,
          padding: (width - longestTickWidth) / 2,
          fill: getChartColor(chartType).stroke,
        },
      };
    },
    [getChartColor, yAxisInformation],
  );

  const chartPadding = useMemo(
    () => ({
      left: yAxisInformation[1]?.width ?? 30,
      top: 5,
      right: yAxisInformation[0].width ?? 60,
      bottom: 40,
    }),
    [yAxisInformation],
  );

  const handleTransactionDotPress = useCallback(
    (
      event: React.SyntheticEvent<any, Event>,
      eventProps: { index: number; data: Datum[]; datum: Datum },
    ) => {
      const { isBuy } = eventProps.datum;
      const isStableValueVisible = selectedChartTypes.includes(
        ChartType.StableBalance,
      );

      const startIndex =
        eventProps.index -
        (isBuy ? (isStableValueVisible ? 0 : 1) : isStableValueVisible ? 1 : 0);
      const endIndex =
        eventProps.index +
        (isBuy ? (isStableValueVisible ? 2 : 1) : isStableValueVisible ? 1 : 2);

      const zoomedTransactionDots = eventProps.data.slice(
        startIndex === -1 ? 0 : startIndex,
        endIndex,
      );

      let minX = domain.x[0] as number;
      const maxX = zoomedTransactionDots[zoomedTransactionDots.length - 1].x;

      if (startIndex !== -1) {
        minX = zoomedTransactionDots[0].x;
      }

      const diffX = maxX - minX;
      const bufferSize = diffX * xDomainBuffer;

      const minDomainX = Math.max(
        minX - bufferSize,
        originalDomainX[0] as number,
      );
      const maxDomainX = Math.min(
        maxX + bufferSize,
        originalDomainX[1] as number,
      );

      const zoomedDomain = {
        x: [minDomainX, maxDomainX] as DomainTuple,
        y: [0, 1] as DomainTuple,
      };

      setZoomedDomain(zoomedDomain);
      setZoomedStartIndex(Math.max(-1, startIndex));
      setZoomLevel(1);

      return null;
    },
    [domain.x, originalDomainX, selectedChartTypes],
  );

  const monthlyProfitDataSets = useMemo(() => {
    /**
     * We start at the beginning of the stableData
     * For the first one, we go until the end of the month
     * For the rest we beginning of month until the end
     * for the last one, beginning of month until now
     */
    const isAssetChartSelected =
      selectedChartTypes.includes(ChartType.AssetBalance) ||
      selectedChartTypes.includes(ChartType.TotalAssetBalance);

    const data = isAssetChartSelected ? totalAssetData : totalStableData;

    if (!data?.length) {
      return [];
    }

    let endTimestamp = Math.min(
      new Date(
        new Date(data[0].x).getFullYear(),
        new Date(data[0].x).getMonth() + 1,
        0,
      ).valueOf(),
      new Date().valueOf(),
    );

    let startBalance = data[0].y;
    let previousBalance = data[0].y;
    const dataSets: Array<{
      data: Array<{
        x: number;
        y: number;
        y0?: number;
        color: string;
        date?: Date;
      }>;
      color?: string;
    }> = [];

    for (let i = 0; i < data.length; i++) {
      const datum = data[i];
      const profit = (datum.y - startBalance) / startBalance;

      if (datum.x <= endTimestamp) {
        if (!dataSets.length) {
          dataSets.push({ data: [] });
        }

        dataSets[dataSets.length - 1].data.push({
          x: datum.x,
          date: new Date(datum.x),
          y: Math.abs(profit),
          color:
            profit >= 0
              ? 'url(#positiveMonthGradient)'
              : 'url(#negativeMonthGradient)',
        });
        previousBalance = datum.y;
      } else {
        if (!dataSets.length) {
          dataSets.push({ data: [] });
        }

        endTimestamp = new Date(
          new Date(datum.x).getFullYear(),
          new Date(datum.x).getMonth() + 1,
          0,
        ).valueOf();
        startBalance = previousBalance;

        if (dataSets[dataSets.length - 1].data.length) {
          dataSets.push({
            data: [
              {
                x: new Date(
                  new Date(datum.x).getFullYear(),
                  new Date(datum.x).getMonth(),
                  0,
                ).valueOf(),
                date: new Date(
                  new Date(datum.x).getFullYear(),
                  new Date(datum.x).getMonth(),
                  0,
                ),
                y: Math.abs(profit),
                color:
                  profit >= 0
                    ? 'url(#positiveMonthGradient)'
                    : 'url(#negativeMonthGradient)',
              },
            ],
          });
        }
      }
    }

    if (!domain) {
      return dataSets;
    }

    /** Update the y value to stretch the first 30% of the y-axis */

    const max = Math.max(
      ...dataSets.map(({ data }) => Math.max(...data.map((datum) => datum.y))),
    );

    const yDomainMin = Math.max(0, domain.y[0] as number);
    const yDomainMax = domain.y[1] as number;

    dataSets.forEach((dataSet) => {
      const { data } = dataSet;

      data.forEach((datum) => {
        const currentY =
          yDomainMin +
          (yDomainMin === 0 ? (yDomainMax - yDomainMin) * 0.03 : 0) +
          (datum.y / max) *
            (yDomainMax - yDomainMin) *
            (yDomainMin === 0 ? 0.15 : 0.05);

        datum.y = currentY;
      });

      if (data.length) {
        const lastDatum = data[data.length - 1];
        dataSet.color = lastDatum.color;
      }
    });

    return dataSets;
  }, [domain, selectedChartTypes, totalAssetData, totalStableData]);

  const referenceLineTimestamp = moment('2026-04-12').valueOf();
  const referenceLineLabel = 'TPT';

  const startTPTReferenceLine = useMemo(() => {
    return [
      { x: referenceLineTimestamp, y: 0 },
      { x: referenceLineTimestamp, y: 1 },
    ];
  }, [referenceLineTimestamp]);

  const startTPTReferenceLabel = useMemo(() => {
    return { x: referenceLineTimestamp, y: 1, label: referenceLineLabel };
  }, [referenceLineTimestamp, referenceLineLabel]);

  return (
    <div
      className="profit-chart-root"
      ref={rootRef}
      style={{
        position: 'relative',
        opacity: rootDimensions ? 1 : 0,
        transition: 'opacity 500ms ease-in-out',
      }}
    >
      <svg style={{ position: 'absolute' }}>
        <defs>
          <linearGradient
            id="positiveStableColor"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor={colors.mossGreen} stopOpacity={0.3} />
            <stop
              offset="100%"
              stopColor={colors.mossGreen}
              stopOpacity={0.04}
            />
          </linearGradient>
          <linearGradient
            id="negativeStableColor"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor={colors.red} stopOpacity={0.4} />
            <stop offset="100%" stopColor={colors.red} stopOpacity={0.08} />
          </linearGradient>
          <linearGradient id="botColor" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={botColor} stopOpacity={0.6} />
            <stop offset="100%" stopColor={botColor} stopOpacity={0.04} />
          </linearGradient>
          <linearGradient id="napColor" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={colors.white} stopOpacity={0.4} />
            <stop offset="100%" stopColor={colors.white} stopOpacity={0.04} />
          </linearGradient>
          <linearGradient
            id="positiveMonthGradient"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor={colors.green} stopOpacity={0.3} />
            <stop offset="100%" stopColor={colors.green} stopOpacity={0.08} />
          </linearGradient>
          <linearGradient
            id="negativeMonthGradient"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor={colors.red} stopOpacity={0.3} />
            <stop offset="100%" stopColor={colors.red} stopOpacity={0.08} />
          </linearGradient>

          <pattern
            id="orangeStripes"
            patternUnits="userSpaceOnUse"
            width="4"
            height="4"
            patternTransform="rotate(45)"
          >
            <line
              x1="0"
              y1="0"
              x2="0"
              y2="4"
              stroke={colors.orange}
              strokeWidth="2"
              opacity="0.7"
            />
          </pattern>
        </defs>
      </svg>

      {!!rootDimensions && (
        <>
          <VictoryChart
            height={rootDimensions.height}
            width={rootDimensions.width}
            scale="linear"
            padding={chartPadding}
            domainPadding={{ y: 5 }}
            domain={domain}
          >
            {selectedChartTypesAxis.map((chartType, index) => (
              <VictoryAxis
                key={`axis_${chartType}`}
                dependentAxis
                style={getYAxisStyle(chartType, index)}
                tickCount={8}
                orientation={index === 0 ? 'right' : 'left'}
                tickFormat={(t: number) => calculateYTickValue(index, t)}
                width={calculateYAxisWidthByChartType(index).width}
              />
            ))}

            <VictoryAxis
              tickFormat={calculateTickValue}
              style={axisStyle}
              tickValues={tickValues}
              tickCount={8}
              fixLabelOverlap
            />

            {animatedDataPoints.map((selectedDataPoint, index) => (
              <VictoryArea
                key={`chart_${selectedChartTypes[index]}`}
                data={selectedDataPoint}
                style={{
                  data: {
                    ...getChartColor(selectedChartTypes[index]),
                    strokeWidth: 1,
                  },
                }}
              />
            ))}

            {!!monthlyProfitDataSets?.length &&
              monthlyProfitDataSets.map(
                ({ data: monthlyProfitDataPoints, color }, index) => (
                  <VictoryArea
                    key={`monthly_profit_${index}`}
                    data={monthlyProfitDataPoints}
                    style={{
                      data: {
                        fill: color,
                        opacity: isAnimatedFinished ? 1 : 0,
                        transition: 'opacity 0.5s',
                      },
                    }}
                  />
                ),
              )}

            <VictoryScatter
              data={transactionDataPoints}
              labelComponent={
                <VictoryTooltip
                  constrainToVisibleArea
                  cornerRadius={3}
                  orientation={'left'}
                  centerOffset={{ x: -10, y: -40 }}
                  pointerOrientation="right"
                  pointerWidth={20}
                  flyoutStyle={{
                    stroke: (d) => d.datum.color,
                    strokeWidth: 1.5,
                    fill: 'rgba(255, 255, 255, 0.8)',
                  }}
                  flyoutPadding={{ left: 20, right: 20, top: 5, bottom: 5 }}
                  labelComponent={
                    <VictoryLabel
                      textAnchor="start"
                      dx={(d) => (d.datum.isBuy === null ? -60 : -55)}
                      style={[
                        {
                          fill: 'rgb(0, 0, 0)',
                          fontFamily: 'monospace',
                          fontSize: 10,
                          fontWeight: 800,
                        },
                      ]}
                    />
                  }
                />
              }
              style={{
                data: {
                  fill: ({ datum }) => datum.color,
                  stroke: colors.white,
                  strokeWidth: 1.5,
                  opacity: isAnimatedFinished ? 1 : 0,
                  transition: 'opacity 0.5s',
                },
              }}
              events={[
                {
                  target: 'data',
                  eventHandlers: {
                    onClick: handleTransactionDotPress,
                  },
                },
              ]}
              size={zoomedDomain ? 4 : 4}
              dataComponent={
                <Point className="single-profit-calcs-chart-scatter-dot" />
              }
            />

            <VictoryLine
              data={startTPTReferenceLine}
              style={{
                data: {
                  stroke: 'url(#orangeStripes)',
                  strokeWidth: 5,
                },
              }}
            />
            <VictoryScatter
              data={[startTPTReferenceLabel]}
              size={0}
              labels={({ datum }) => datum.label}
              labelComponent={
                <VictoryLabel
                  style={{
                    fill: colors.orange,
                    fontWeight: 'bold',
                    fontSize: 10,
                    opacity: 0.7,
                  }}
                  textAnchor="end"
                  dy={8}
                  dx={-8}
                />
              }
            />
          </VictoryChart>
          <ChartOptions
            selectedChartTypes={selectedChartTypes}
            setSelectedChartTypes={setSelectedChartTypes}
            showAccumulativeData={showAccumulativeData}
            setShowAccumulativeData={setShowAccumulativeData}
            totalBalanceColor={
              getChartColor(ChartType.TotalStableBalance).stroke
            }
            totalAssetBalanceColor={
              getChartColor(ChartType.TotalAssetBalance).stroke
            }
            noAlgorithmicProgressionColors={
              getChartColor(ChartType.NoAlgoritmicProgression).stroke
            }
          />
        </>
      )}
    </div>
  );
};

export default ProfitChart;
